/**
 * IMPORTS
 */
import {OpenFeature} from '@openfeature/web-sdk';
import {subscribeCustomerTagAdded}
    from 'src/aggregates/customers/subscriptions/utils';
import {subscribeCustomerTagRemoved}
    from 'src/aggregates/customers/subscriptions/utils';
import {unsubscribeCustomerTagAdded}
    from 'src/aggregates/customers/subscriptions/utils';
import {unsubscribeCustomerTagRemoved}
    from 'src/aggregates/customers/subscriptions/utils';
import {execute} from 'src/aggregates/execute';
import * as commands from 'src/aggregates/user/commands';
import * as events from 'src/aggregates/user/events';
import {subscribeKanbanUpdated} from 'src/aggregates/user/subscriptions/utils';
import {unsubscribeKanbanUpdated}
    from 'src/aggregates/user/subscriptions/utils';
import {authenticate} from 'src/aggregates/user/utils';
import {authenticateToken} from 'src/aggregates/user/utils';
import {authorize} from 'src/aggregates/user/utils';
import {getCompanyConfig} from 'src/aggregates/user/utils';
import {getKanbans} from 'src/aggregates/user/utils';
import {getUserConfig} from 'src/aggregates/user/utils';
import {getServer} from 'src/aggregates/user/utils';
import {reduceQuickMessages} from 'src/aggregates/user/utils';
import {start as userStart} from 'src/aggregates/user/utils';
import {updatePassword as updateUserPassword} from 'src/aggregates/user/utils';
import {updateUserConfig} from 'src/aggregates/user/utils';
import {logger} from 'src/logger';
import GraphisClient from 'src/services/graphis';


/**
 * TYPES
 */
import {IAppState} from 'src/aggregates/index.d';
import {IEvent} from 'src/aggregates/index.d';
import {IUserClearPopup} from 'src/aggregates/user/commands.d';
import {IUserFetchKanbans} from 'src/aggregates/user/commands.d';
import {IUserLoadConfig} from 'src/aggregates/user/commands.d';
import {IUserLogin} from 'src/aggregates/user/commands.d';
import {IUserLoginToken} from 'src/aggregates/user/commands.d';
import {IUserLogout} from 'src/aggregates/user/commands.d';
import {IUserNotify} from 'src/aggregates/user/commands.d';
import {IUserReplaceKanbanMetadata} from 'src/aggregates/user/commands.d';
import {IUserSetServerUrl} from 'src/aggregates/user/commands.d';
import {IUserShowPopup} from 'src/aggregates/user/commands.d';
import {IUserStart} from 'src/aggregates/user/commands.d';
import {IUserUpdateConfig} from 'src/aggregates/user/commands.d';
import {IUserUpdateKanbanMetadata} from 'src/aggregates/user/commands.d';
import {IUserUpdatePassword} from 'src/aggregates/user/commands.d';
import {IUserUpdateStatus} from 'src/aggregates/user/commands.d';
import {IGraphisCompanyConfig} from 'src/aggregates/user/queries.d';
import {ICompanyConfig} from 'src/aggregates/user/state.d';
import {IKanbanByTag} from 'src/aggregates/user/state.d';
import {loginErrors} from 'src/aggregates/user/state.d';
import {ILoginTokenResponse} from 'src/aggregates/user/utils.d';
import {PreAuthorizationError}
    from 'src/aggregates/user/err/preauthorizationerror';


/**
 * CONSTANTS AND DEFINITIONS
 */
const LOCAL_STORAGE_SERVER_KEY = '@pecazap:server';
const LOCAL_STORAGE_SESSION_TOKEN = '@pecazap:token';


/**
 * CODE
 */

/**
 * I am an user clear popup command handler.
 *
 * :param command: user clear popup command
 * :param state: application state
 *
 * :generates: events
 */
async function* clearPopup (command: IUserClearPopup,
                            state: IAppState): AsyncIterable<IEvent>
{
    yield events.userPopupCleared();
}


/**
 * I am an user fetch kanbans command handler.
 *
 * :param command: user fetch kanbans command
 * :param state: application state
 *
 * :generates: events
 */
async function* fetchKanbans (
    command: IUserFetchKanbans,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // generate loading event
    yield events.userKanbansFetching();

    // fetch kanbans
    const result = await getKanbans();

    // failed: generate not fetched event
    if (result === null)
    {
        yield events.userKanbansNotFetched();
        return;
    }

    // unsubscribe old kanban
    unsubscribeKanbanUpdated();

    // unsubscribe old tags
    const oldTags = Object.keys(state.user.kanban.byTag);
    oldTags.forEach(tag =>
    {
        unsubscribeCustomerTagAdded(Number(tag));
        unsubscribeCustomerTagRemoved(Number(tag));
    });

    // subscribe kanban updates
    subscribeKanbanUpdated();

    // reduce kanbans by tag
    const byTag: Record<number, IKanbanByTag> = {};
    result.forEach(kanban =>
    {
        subscribeCustomerTagAdded(kanban.tag);
        subscribeCustomerTagRemoved(kanban.tag);

        byTag[kanban.tag] = {
            columnName: kanban.name,
            customers: [],
            hasError: false,
            hasMore: true,
            loading: false,
            page: 0,
            prio: kanban.prio,
            tag: kanban.tag,
        };
    });

    // emit loaded event
    yield events.userKanbansFetched(byTag);
}


/**
 * I am a user config command handler.
 *
 * :param command: user config command
 * :param state: application state
 *
 * :generates: events
 */
async function* loadConfig (command: IUserLoadConfig,
                            state: IAppState): AsyncIterable<IEvent>
{
    // emit loading event
    yield events.userConfigLoading();

    // get user config
    const config = await getUserConfig();

    // user config not found: emit not loaded event and return
    if (config === null)
    {
        yield events.userConfigNotLoaded();
        return;
    }

    // reduce quick messages
    config.messages = reduceQuickMessages(
        config.messages,
        state.user.isAdmin,
    );

    // emit loaded event
    yield events.userConfigLoaded(config);
}


/**
 * I am an user login command handler.
 *
 * :param command: user login command
 * :param state: application state
 *
 * :generates: events
 */
async function* login (command: IUserLogin,
                       state: IAppState): AsyncIterable<IEvent>
{
    // generate user logging in event
    yield events.userLoggingIn();

    // initialize last logged server
    let server: string;

    // get command props
    const {isAdmin, password, username} = command;

    // log login try
    logger.info(
        'CVJPZ0047I',
        {
            role: isAdmin === true ? 'supervisor' : 'agent',
            user: username,
        },
    );

    // get server to authenticate with
    try
    {
        server = await getServer(isAdmin, password, username);
    }

    // no server responded: log error, generate failure and return
    catch (error)
    {
        // log error
        logger.error('CVJPZ0044E', {reason: error, user: username});

        // initialize error reason
        let reason = loginErrors.AUTHENTICATION;

        // authorization error: update error reason
        if (error instanceof PreAuthorizationError)
        {
            reason = loginErrors.AUTHORIZATION;
        }

        // other error: clear last server from local storage
        else
        {
            localStorage.removeItem(LOCAL_STORAGE_SERVER_KEY);
        }

        // generate failure event and return
        yield events.userNotLoggedIn(reason);
        return;
    }

    // add last server to local storage
    localStorage.setItem(LOCAL_STORAGE_SERVER_KEY, server);

    // format base url
    const url = `https://${server}`;

    // request authentication on XMPP
    const authError = await authenticate(password, url, username);

    // authentication failed: generate user not logged event in and return
    if (authError !== null)
    {
        // generate user not logged event in
        yield events.userNotLoggedIn(loginErrors.AUTHENTICATION);

        // clear last server from local storage and return
        localStorage.removeItem(LOCAL_STORAGE_SERVER_KEY);
        return;
    }

    // authentication succeeded: authorize
    const authorization = await authorize(isAdmin, server, username);

    // authorization failed: generate user not logged in event and return
    if (authorization === null)
    {
        yield events.userNotLoggedIn(loginErrors.AUTHORIZATION);
        return;
    }

    // save last url logged in
    localStorage.setItem(LOCAL_STORAGE_SERVER_KEY, server);

    // save session token
    localStorage.setItem(LOCAL_STORAGE_SESSION_TOKEN, authorization.user.token);

    // create graphis client with connected base url
    GraphisClient.create(server,
                         authorization.user.company,
                         authorization.user.token);

    // re-fetch kanbans when the subscription client reconnects
    GraphisClient.onReconnected(() =>
    {
        execute(commands.fetchKanbans(), fetchKanbans);
    });

    // generate user server url set event
    yield events.userServerUrlSet(url);

    // get company config
    const company: IGraphisCompanyConfig = await getCompanyConfig();

    // request failed: generate user not logged in event and return
    if (company === null)
    {
        yield events.userNotLoggedIn(loginErrors.UNAVAILABLE);
        return;
    }

    // update open feature to use company context
    OpenFeature.setContext({targetingKey: company.flagUserId ?? ''});

    // set customers visibility according to config
    const companyConfig: ICompanyConfig = {
        partner: company.partner,
        telephony: company.telephony,
        visibility: {
            customers: {
                noWallet: company.customersVisibility.noWallet,
                onlyOwn: company.customersVisibility.onlyOwn,
            },
        },
    };

    // generate user logged in event and return
    yield events.userLoggedIn(
        authorization.user.id,
        authorization.user.company,
        companyConfig,
        authorization.user.name,
        authorization.rooms,
        username,
        isAdmin,
    );
}


/**
 * I am an user login token command handler.
 *
 * :param command: user login token command
 * :param state: application state
 *
 * :generates: events
 */
async function* loginToken (command: IUserLoginToken,
                            state: IAppState): AsyncIterable<IEvent>
{
    // generate user logging in event
    yield events.userLoggingIn();

    // get command props
    let {role, token} = command;

    // make role from plural to singular
    role = role.slice(0,-1);

    // get is Admin variable
    const isAdmin = role === 'supervisor' ? true : false;

    // get last server from local storage
    const server = localStorage.getItem(LOCAL_STORAGE_SERVER_KEY);

    // servir key does not exist: fail login
    if (server == null)
    {
        // clear last token from local storage
        localStorage.removeItem(LOCAL_STORAGE_SESSION_TOKEN);

        // generate user not logged in event
        yield events.userNotLoggedIn(loginErrors.AUTHENTICATION);
        return;
    }

    // initialize last logged server
    let response: ILoginTokenResponse;

    // authenticate with token
    try
    {
        response = await authenticateToken(role, server, token);
    }

    // authentication failed: generate failure and return
    catch (error)
    {
        // clear last token from local storage
        localStorage.removeItem(LOCAL_STORAGE_SESSION_TOKEN);

        // generate user not logged in event
        yield events.userNotLoggedIn(loginErrors.AUTHENTICATION);
        return;
    }

    // get user params
    const {password, username} = response;

    //get new token
    token = response.token;

    // save new token on local storage
    localStorage.setItem(LOCAL_STORAGE_SESSION_TOKEN, token);

    // format base url
    const url = `https://${server}`;

    // request authentication on XMPP
    const authError = await authenticate(password, url, username);

    // authentication failed: generate user not logged in event and return
    if (authError !== null)
    {
        // clear last token from local storage
        localStorage.removeItem(LOCAL_STORAGE_SESSION_TOKEN);

        // generate user not logged in event
        yield events.userNotLoggedIn(loginErrors.AUTHENTICATION);
        return;
    }

    // authentication succeeded: authorize
    const authorization = await authorize(isAdmin, server, username);

    // authorization failed: generate user not logged in event and return
    if (authorization === null)
    {
        // clear last token from local storage
        localStorage.removeItem(LOCAL_STORAGE_SESSION_TOKEN);

        // generate user not logged in event
        yield events.userNotLoggedIn(loginErrors.AUTHORIZATION);
        return;
    }

    // save last url logged in
    localStorage.setItem(LOCAL_STORAGE_SERVER_KEY, server);

    // save session token
    localStorage.setItem(LOCAL_STORAGE_SESSION_TOKEN, authorization.user.token);

    // create graphis client with connected base url
    GraphisClient.create(server,
                         authorization.user.company,
                         authorization.user.token);

    // re-fetch kanbans when the subscription client reconnects
    GraphisClient.onReconnected(() =>
    {
        execute(commands.fetchKanbans(), fetchKanbans);
    });

    // generate user server url set event
    yield events.userServerUrlSet(url);

    // get company config
    const company: IGraphisCompanyConfig = await getCompanyConfig();

    // request failed: generate user not logged in event and return
    if (company === null)
    {
        yield events.userNotLoggedIn(loginErrors.UNAVAILABLE);
        return;
    }

    // update open feature to use company context
    OpenFeature.setContext({targetingKey: company.flagUserId ?? ''});

    // set customers visibility according to config
    const companyConfig: ICompanyConfig = {
        partner: company.partner,
        telephony: company.telephony,
        visibility: {
            customers: {
                noWallet: company.customersVisibility.noWallet,
                onlyOwn: company.customersVisibility.onlyOwn,
            },
        },
    };

    // generate user logged in event and return
    yield events.userLoggedIn(
        authorization.user.id,
        authorization.user.company,
        companyConfig,
        authorization.user.name,
        authorization.rooms,
        username,
        isAdmin,
    );
}


/**
 * I am an user logout command handler.
 *
 * :param command: user logout command
 * :param state: application state
 *
 * :generates: events
 */
async function* logout (command: IUserLogout,
                        state: IAppState): AsyncIterable<IEvent>
{
    // unsubscribe kanban tags
    const tags = Object.keys(state.user.kanban.byTag);
    tags.forEach(tag =>
    {
        unsubscribeCustomerTagAdded(Number(tag));
        unsubscribeCustomerTagRemoved(Number(tag));
    });

    // unsubscribe kanban updates
    unsubscribeKanbanUpdated();

    // close graphis subscription client to stop its reconnection cycle
    GraphisClient.close();

    // remove session token from local storage
    localStorage.removeItem(LOCAL_STORAGE_SESSION_TOKEN);

    // generate user logged out event
    yield events.userLoggedOut();
}


/**
 * I am an user notify command handler.
 *
 * :param command: user notify command
 * :param state: application state
 *
 * :generates: events
 */
async function* notify (command: IUserNotify,
                        state: IAppState): AsyncIterable<IEvent>
{
    // generate user notified event
    yield events.userNotified(command.message,
                              command.reference,
                              command.requireInteraction,
                              command.title);
}


/**
 * I am an user-replace-kanban-metadata command handler.
 *
 * :param command: user-replace-kanban-metadata command
 * :param state: application state
 *
 * :generates: events
 */
async function* replaceKanbanMetadata (
    command: IUserReplaceKanbanMetadata,
    state: IAppState,
): AsyncIterable<IEvent>
{
    yield events.userKanbansReplaced(command.byTag);
}


/**
 * I am an user set server url command handler.
 *
 * :param command: user set server url command
 * :param state: application state
 *
 * :generates: events
 */
async function* setServerUrl (
    command: IUserSetServerUrl,
    state: IAppState,
): AsyncIterable<IEvent>
{
    yield events.userServerUrlSet(command.serverUrl);
}


/**
 * I am an user show popup command handler.
 *
 * :param command: user show popup command
 * :param state: application state
 *
 * :generates: events
 */
async function* showPopup (command: IUserShowPopup,
                           state: IAppState): AsyncIterable<IEvent>
{
    yield events.userPopupShown(command.level,
                                command.message,
                                command.title);
}


/**
 * I am an user start command handler.
 *
 * :param command: user start command
 * :param state: application state
 *
 * :generates: events
 */
async function* start (command: IUserStart,
                       state: IAppState): AsyncIterable<IEvent>
{
    // emit starting event
    yield events.userStarting();

    // start application
    const result = await userStart(state.user.id, state.user.rooms);

    // start failed: emit event and abort
    if (result === null)
    {
        yield events.userNotStarted();
        return;
    }

    // start successful: emit user started event
    yield events.userStarted();
}


/**
 * I am a user config update command handler.
 *
 * :param command: load command
 * :param state: application state
 *
 * :generates: events
 */
async function* updateConfig (
    command: IUserUpdateConfig,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // emit updating event
    yield events.userConfigUpdating();

    // update user config
    const config = await updateUserConfig(command.config);

    // update user config failed: emit not updated event and return
    if (config === null)
    {
        yield events.userConfigNotUpdated();
        return;
    }

    // emit updated event
    yield events.userConfigUpdated(config);
}


/**
 * I am an user update kanban metadata command handler.
 *
 * :param command: user update kanban metadata command
 * :param state: application state
 *
 * :generates: events
 */
async function* updateKanbanMetadata (
    command: IUserUpdateKanbanMetadata,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // column doesn't exist (removed or not fetched): drop the update,
    // otherwise the reducer would create a phantom column for the tag
    if (state.user.kanban.byTag[command.tag] === undefined)
    {
        return;
    }

    // emit updated event
    yield events.userKanbanMetadataUpdated(command.tag, command.metadata);
}


/**
 * I am an user update password command handler.
 *
 * :param command: command props
 * :param state: application state
 *
 * :generates: events
 */
async function* updatePassword (
    command: IUserUpdatePassword,
    state: IAppState,
): AsyncIterable<IEvent>
{
    // already is updating: stop
    if (state.user.isUpdatingPassword === true)
    {
        return;
    }

    // emit updating event
    yield events.userPasswordUpdating();

    // update user password
    const result = await updateUserPassword(command.old, command._new);

    // failed: emit not updated event and return
    if (result === null)
    {
        yield events.userPasswordNotUpdated();
        return;
    }

    // emit updated event
    yield events.userPasswordUpdated();
}


/**
 * I am an user update status command handler.
 *
 * :param command: user update status command
 * :param state: application state
 *
 * :generates: events
 */
async function* updateStatus (command: IUserUpdateStatus,
                              state: IAppState): AsyncIterable<IEvent>
{
    yield events.userStatusUpdated(command.status);
}


/**
 * EXPORTS
 */
export {
    clearPopup,
    fetchKanbans,
    loadConfig,
    login,
    loginToken,
    logout,
    notify,
    replaceKanbanMetadata,
    setServerUrl,
    showPopup,
    start,
    updateConfig,
    updateKanbanMetadata,
    updatePassword,
    updateStatus,
};
